// Thin typed *views* over the DICOM JSON Model (not a parallel invented model).
// Each carries the raw DicomJson so callers can reach any tag we didn't surface.

import {
  Tag,
  formatDicomDate,
  formatPersonName,
  getNumber,
  getPersonName,
  getString,
  getStrings,
  type DicomJson,
} from './dicomTags';
import { isDerivedModality } from './derivedSeries';

export interface Study {
  studyInstanceUID: string;
  patientId: string;
  patientName: string; // display-formatted
  studyDate: string; // display-formatted (YYYY-MM-DD)
  accessionNumber: string;
  description: string;
  modalities: string[];
  numberOfSeries?: number;
  numberOfInstances?: number;
  raw: DicomJson;
}

export interface Series {
  seriesInstanceUID: string;
  studyInstanceUID: string;
  modality: string;
  seriesNumber?: number;
  description: string;
  numberOfInstances?: number;
  raw: DicomJson;
}

export interface Instance {
  sopInstanceUID: string;
  seriesInstanceUID: string;
  studyInstanceUID: string;
  sopClassUID: string;
  instanceNumber?: number;
  rows?: number;
  columns?: number;
  numberOfFrames?: number;
  raw: DicomJson;
}

/** A worklist "case" — a derived grouping of studies (no backend entity). */
export interface Case {
  caseId: string;
  patientId: string;
  patientName: string;
  studies: Study[];
  latestStudyDate: string;
  modalities: string[];
}

export function toStudy(ds: DicomJson): Study {
  return {
    studyInstanceUID: getString(ds, Tag.StudyInstanceUID) ?? '',
    patientId: getString(ds, Tag.PatientID) ?? '',
    patientName: formatPersonName(getPersonName(ds, Tag.PatientName)),
    studyDate: formatDicomDate(getString(ds, Tag.StudyDate)),
    accessionNumber: getString(ds, Tag.AccessionNumber) ?? '',
    description: getString(ds, Tag.StudyDescription) ?? '',
    // What an AI model made from a study's images (SEG, SR) is a series of
    // the study to every DICOM client, but never an image here: the viewer's
    // lists name only the modalities of what they show.
    modalities: getStrings(ds, Tag.ModalitiesInStudy).filter((m) => !isDerivedModality(m)),
    numberOfSeries: getNumber(ds, Tag.NumberOfStudyRelatedSeries),
    numberOfInstances: getNumber(ds, Tag.NumberOfStudyRelatedInstances),
    raw: ds,
  };
}

/** A human-friendly name for a series/slide: its description, else "Series N"
 *  from the series number, else a positional "Slide N" (WSI) / "Series N". Avoids
 *  ever surfacing the raw SeriesInstanceUID as the primary label. */
export function seriesDisplayName(s: Series, index: number): string {
  const desc = s.description?.trim();
  if (desc) return desc;
  if (s.seriesNumber != null) return `Series ${s.seriesNumber}`;
  const kind = s.modality === 'SM' ? 'Slide' : 'Series';
  return index >= 0 ? `${kind} ${index + 1}` : kind;
}

export function toSeries(ds: DicomJson): Series {
  return {
    seriesInstanceUID: getString(ds, Tag.SeriesInstanceUID) ?? '',
    studyInstanceUID: getString(ds, Tag.StudyInstanceUID) ?? '',
    modality: getString(ds, Tag.Modality) ?? '',
    seriesNumber: getNumber(ds, Tag.SeriesNumber),
    description: getString(ds, Tag.SeriesDescription) ?? '',
    numberOfInstances: getNumber(ds, Tag.NumberOfSeriesRelatedInstances),
    raw: ds,
  };
}

export function toInstance(ds: DicomJson): Instance {
  return {
    sopInstanceUID: getString(ds, Tag.SOPInstanceUID) ?? '',
    seriesInstanceUID: getString(ds, Tag.SeriesInstanceUID) ?? '',
    studyInstanceUID: getString(ds, Tag.StudyInstanceUID) ?? '',
    sopClassUID: getString(ds, Tag.SOPClassUID) ?? '',
    instanceNumber: getNumber(ds, Tag.InstanceNumber),
    rows: getNumber(ds, Tag.Rows),
    columns: getNumber(ds, Tag.Columns),
    numberOfFrames: getNumber(ds, Tag.NumberOfFrames),
    raw: ds,
  };
}
